import { type AppProps } from "next/app";
import { useRouter } from "next/router";
import Script from "next/script";
import { useCallback, useEffect, useMemo } from "react";
import { css, Global, ThemeProvider, CacheProvider } from "@emotion/react";
import { polyfill } from "smoothscroll-polyfill";
import Cookies from "js-cookie";
import { ApolloProvider } from "@apollo/react-hooks";
import { getApolloClient } from "src/lib/apollo-client";
import { Page } from "src/atoms/Page";
import { theme } from "src/theme";
import GlobalStyles from "src/GlobalStyles";
import { sourceSans3 } from "src/fonts/source-sans-3";
import { isClient } from "src/utils/isClient";
import type { FeatureFlags } from "src/types/featureFlags";
import { AppBootstrap } from "src/contexts/app-bootstrap";
import {
  locale as appLocale,
  constructorIndexKey,
  constructorBeaconBundle,
} from "src/config/app-config";
import { useUserPreferences } from "src/hooks/stores/use-app-store";
import { TranslationProvider } from "src/providers/TranslationProvider";
import { useUserWishlistMeta } from "src/hooks/useUserWishlistMeta";
import { useSessionError } from "src/hooks/useSessionError";
import type { Session } from "next-auth";
import { getSession, SessionProvider, useSession } from "next-auth/react";
import NativeAppLayout from "src/Layouts/NativeAppLayout";
import { useUserProfile } from "src/hooks/useUserProfile";
import { updateSession } from "src/utils/updateSession";
import { GoogleOneTap } from "src/atoms/GoogleOneTap";
import { PageViewTracker } from "src/atoms/PageViewTracker";
import { ConsentManagerBanner } from "src/molecules/ConsentMangerBanner";
import { useNavigationItems } from "src/hooks/useNavigationItems";
import NavigationContext, {
  type NavigationState,
} from "src/contexts/navigation.context";
import type { NavigationItems, SeoQueryResponse } from "src/types/graphql.d";
import {
  ShopType,
  localeFixOptional,
} from "src/utils/genderLocale/genderLocale";
import { EppoRandomizationProvider } from "src/providers/EppoRandomizationProvider";
import type { SerializedListingResult } from "src/types/product-listing";
import { UserPreferences } from "src/atoms/UserPreferences";
import { AuthProvider } from "src/pages/api/auth/getUserTokenFromApi";
import { PageHead } from "src/molecules/PageHead";
import {
  OTRIUM_NO_GATE_ENABLED,
  URL_PARAM_EXTERNAL_SOURCE,
} from "src/constants/gatedItem";
import createCache from "@emotion/cache";
import { CartBootstrap } from "src/contexts/cart-bootstrap";
import { PageViewedContextProvider } from "src/contexts/page-viewed.context";
import { isAndroidApp, isIOSApp } from "src/utils/isNativeApp";
import { OT_SHOP_TYPE_COOKIE } from "src/utils/constants";
import { ShopTypeContextProvider } from "src/contexts/shop-type.context";
import { SchemaProvider } from "src/contexts/schema.context";
import { SchemaInjector } from "src/organisms/SchemaInjector";
import Coframe from "src/utils/coframe";

import "@otrium/design/styles.css";
import "swiper/swiper-bundle.min.css";
import "rc-slider/assets/index.css";
import "./global.css";
import "src/styles/layout.scss";

export const emotionCache = createCache({ key: "otrium" });

if (isClient) {
  /**
   * NOTE: Polyfill to fix Swiper smooth animation in Safari
   *
   * Accordint to Swiper API (https://swiperjs.com/api/) with cssMode={true}:
   * "Changing slides with anything except touch/swipe and mousewheel will happen without
   * transition in browsers without scrollTo.behaviour = 'smooth' support (e.g. in desktop and iOS Safari)"
   */
  polyfill(); // eslint-disable-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
}

const AppContainer = (props: Props) => {
  const { Component, ...pageProps } = props;

  const featureFlags = pageProps.pageProps?.featureFlags;

  const { data: session } = useSession();
  const router = useRouter();

  // Force refresh on open tabs that might go to old builds
  // causing 404's
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handleRouteError = (
      err: Error & { cancelled?: boolean },
      url: string
    ) => {
      if (err.cancelled) {
        return;
      }

      location.assign(url);
    };

    router.events.on("routeChangeError", handleRouteError);

    return () => {
      router.events.off("routeChangeError", handleRouteError);
    };
  }, [router.events]);

  // When the tab is refocused, check whether a new build has been deployed and
  // reload onto it. Catches tabs left open across a release before the user
  // hits a broken client navigation against the old build.
  useEffect(() => {
    if (
      typeof window === "undefined" ||
      process.env.NODE_ENV !== "production"
    ) {
      return;
    }

    const checkBuildId = async () => {
      try {
        const res = await fetch("/api/build-id", { cache: "no-store" });
        const { buildId } = (await res.json()) as { buildId: string | null };

        if (buildId && buildId !== window.__NEXT_DATA__.buildId) {
          location.reload();
        }
      } catch {
        // Network error — ignore and retry on the next refocus
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        return;
      }

      void checkBuildId();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const userPreferences = useUserPreferences();
  const shopType = useMemo(() => {
    const pageGender = pageProps?.pageProps?.gender;
    const shopTypeFromUrl = localeFixOptional({
      gender: typeof pageGender === "string" ? pageGender : undefined,
    });
    if (shopTypeFromUrl) return shopTypeFromUrl;

    const cookieShopType = Cookies.get(OT_SHOP_TYPE_COOKIE);
    if (cookieShopType) return cookieShopType;

    return ShopType.All;
  }, [pageProps?.pageProps?.gender]);
  const { data: navigationItemsData, loading: navigationItemsDataLoading } =
    useNavigationItems(shopType);

  const { data: userData } = useUserProfile();

  const navigationState: NavigationState = {
    navigationItems: navigationItemsData?.navigationItems,
    loading: navigationItemsDataLoading || false,
  };

  useEffect(() => {
    // Force the session reload that usually happens   only when you change tabs/windows
    // Reference: https://github.com/nextauthjs/next-auth/issues/596#issuecomment-943453568
    const triggerSessionReload = () => {
      if (!document.hidden) {
        void getSession({ triggerEvent: true });
      }
    };

    document.addEventListener(
      "visibilitychange",
      () => triggerSessionReload,
      false
    );
    return () => {
      window.removeEventListener("visibilitychange", triggerSessionReload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSessionUpdate = useCallback(() => {
    if (userData?.userProfile.token) {
      void updateSession({ token: userData.userProfile.token });
    }
  }, [userData?.userProfile.token]);

  useEffect(() => {
    window.addEventListener("visibilitychange", handleSessionUpdate);

    return () => {
      window.removeEventListener("visibilitychange", handleSessionUpdate);
    };
  }, [handleSessionUpdate]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.cnstrc = window.cnstrc || {};

    if (constructorIndexKey) {
      window.cnstrc.indexKey = constructorIndexKey;
    }

    if (session?.user.member_id) {
      window.cnstrc.userId = session?.user.member_id;
    }
  }, [session?.user.member_id]);

  useUserWishlistMeta();
  useSessionError();

  const isNativeApp = useMemo(
    () =>
      isIOSApp ||
      isAndroidApp ||
      router.query.hasOwnProperty("app") ||
      router.route === "/app-login",
    [router.route, router.query]
  );

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const PageLayout = pageProps?.pageProps?.notFound
    ? Page
    : // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      Component.Layout || Page;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  // Layout is decided per-route at pre-render time, not from client-only
  // signals: /app/* pages declare NativeAppLayout via Component.Layout, and
  // /app-login keeps its dedicated app layout. This avoids the header flash a
  // client-side layout swap caused on webview surfaces (checkout, etc.).
  const Layout = router.route === "/app-login" ? NativeAppLayout : PageLayout;

  return (
    <>
      <AppBootstrap featureFlags={featureFlags} />
      {session?.user.signInPlatform !== AuthProvider.GUEST_LOGIN && (
        <UserPreferences />
      )}
      <SchemaProvider>
        <CartBootstrap />
        <PageViewedContextProvider>
          <ShopTypeContextProvider
            gender={pageProps?.pageProps?.gender || ""}
            preferredShopType={userPreferences?.gender || ""}
            isCartOrFavorites={pageProps?.pageProps?.isCartOrFavorites || false}
          >
            <NavigationContext.Provider
              value={{
                navigationItems: !pageProps?.pageProps.clientNavigation
                  ? pageProps?.pageProps.navigationItems ||
                    navigationState.navigationItems
                  : undefined,
                loading: false,
              }}
            >
              <EppoRandomizationProvider locale={appLocale}>
                <ThemeProvider theme={theme}>
                  <PageViewTracker locale={appLocale} />
                  <GoogleOneTap />
                  {/* Constructor tracking beacon — skipped in the native
                        iOS/Android WebView: the apps track Constructor
                        themselves, so loading it here double-counts every
                        search/browse/click plus the beacon's session events. */}
                  {constructorBeaconBundle && !isNativeApp ? (
                    <Script
                      id="constructor-beacon"
                      src={`https://cnstrc.com/js/cust/${constructorBeaconBundle}.js`}
                      strategy="afterInteractive"
                    />
                  ) : null}
                  <Global styles={css(GlobalStyles)} />
                  <Global
                    styles={css`
                      :root {
                        --font-source-sans-3: ${sourceSans3.style.fontFamily};
                      }
                      body {
                        font-family: ${theme.fonts.body};
                      }
                    `}
                  />
                  <Layout>
                    <CacheProvider value={emotionCache}>
                      <Coframe />
                      <Component {...pageProps} />
                    </CacheProvider>
                  </Layout>
                  <ConsentManagerBanner />
                  <SchemaInjector />
                </ThemeProvider>
              </EppoRandomizationProvider>
            </NavigationContext.Provider>
          </ShopTypeContextProvider>
        </PageViewedContextProvider>
      </SchemaProvider>
    </>
  );
};

const ALLOWED_PATHS = [
  "/[slug]",
  "/[slug]/[brandOrCategoriesSlug]/[...categories]",
  "/[slug]/[brandOrCategoriesSlug]",
  "/[slug]/new",
  "/[slug]/designer",
  "/[slug]/hot-deals",
  "/[slug]/sale",
  "/[slug]/products",
  "/product/[productSlug]",
];

interface Props extends AppProps {
  pageProps: {
    session?: Session;
    isCartOrFavorites?: boolean;
    clientNavigation?: boolean; // Temporary fix to load correct navigation bar, until _app.tsx is refactored.
    navigationItems?: NavigationItems;
    gender: string;
    seoContent?: SeoQueryResponse | null;
    featureFlags?: FeatureFlags;
    initialListingState?: SerializedListingResult;
  };
}

const OtriumApp = (props: Props) => {
  const pathName = props.router.pathname;
  const isGYes = props.router.query.hasOwnProperty(
    URL_PARAM_EXTERNAL_SOURCE as string
  );
  const enableIndexation = !!ALLOWED_PATHS.includes(pathName);
  // A canonical alongside noindex is a conflicting signal, and every ?q= variant
  // would point at the same href anyway (STORE-1342).
  const isSearchPage = pathName.startsWith("/search");

  useEffect(() => {
    if (isGYes) {
      localStorage.setItem(OTRIUM_NO_GATE_ENABLED as string, "true");
    }
  }, [isGYes]);

  return (
    <ApolloProvider client={getApolloClient()}>
      <PageHead
        title="Otrium"
        metaTags={
          <meta name="viewport" content="width=device-width, initial-scale=1" />
        }
        enableIndexation={enableIndexation}
        includeCanonical={!isSearchPage}
        seoContent={props?.pageProps?.seoContent}
        totalPages={props?.pageProps?.initialListingState?.totalPages}
      />
      <SessionProvider session={props?.pageProps?.session}>
        <TranslationProvider>
          <AppContainer {...props} />
        </TranslationProvider>
      </SessionProvider>
    </ApolloProvider>
  );
};

export default OtriumApp;
